import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { UserProfile, UserProfileEntity } from "@modules/auth/userProfileTypes"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useProfileAvatarFormik } from "@modules/profile/myProfile/hooks/useProfileAvatarFormik"
import { Circles } from "@modules/ui/components/circles"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUText } from "@modules/ui/components/huText"
import { useEffect } from "react"
import { useTheme } from "styled-components"

type Props = {
  user: UserProfile
}

export const ProfileAvatarForm: React.FC<Props> = ({ user }) => {
  const { formik, isSubmitting } = useProfileAvatarFormik(user)
  const theme = useTheme()

  useEffect(() => {
    if (formik.values.avatar.localBase64) formik.submitForm()
  }, [formik.values.avatar.localBase64])

  const completion = UserProfileEntity.getProfileCompletion(user)
  const isOnlyMissingAvatar = UserProfileEntity.isOnlyMissingAvatar(user)
  return (
    <div className="card mb-0 bg-neutralColor-100 flex flex-column align-items-center">
      {/* Profile avatar input */}
      <div className="flex flex-row align-items-center justify-content-center w-full gap-4">
        <FormikInput
          name="avatar"
          type="avatar"
          isRequiredInput
          disabled={isSubmitting}
          avatarProps={{
            username: user.username,
          }}
          getFieldProps={formik.getFieldProps}
          setFieldValue={formik.setFieldValue}
          isInvalid={isFormFieldInValid("avatar", formik)}
          error={getFormErrorMessage("avatar", formik)}
        />
        <div className="flex flex-column align-items-start overflow-hidden" style={{ maxWidth: "60%" }}>
          <HUText
            fontStyle="HS"
            numberOfLine={2}
            className="inline-block w-full overflow-hidden text-overflow-ellipsis"
          >
            {`${user.firstname} ${user.lastname}`}
          </HUText>
          <HUText fontStyle="BM">{`@${user.username}`}</HUText>
        </div>
      </div>

      {/* Profile completion ratio */}
      {completion.pcRatio !== 1 &&
        (isOnlyMissingAvatar ? (
          <div className="flex flex-row justify-content-center align-items-center gap-2">
            <WarningHexagonalIcon color={theme.error} size={24} />
            <HUText fontStyle="LS">{word("profile.form.avatar_missing")}</HUText>
          </div>
        ) : (
          <div className="flex flex-column gap-2">
            <Circles count={10} filled={completion.pcRatio * 10} color={theme.textPrimary} progressionstyle="line" />
            <HUText fontStyle="LS" className="text-center">
              {word("profile.form.completion", { percentage: Math.ceil(completion.pcRatio * 100) })}
            </HUText>
          </div>
        ))}
    </div>
  )
}
